import { useState } from 'react';
import { api, ApiFailure, type Report } from '../api';
import { Notice } from './Panels';

type Mode = 'template' | 'push' | 'csv';

/**
 * Power BI integration — export datasets and reports in formats Power BI can
 * consume, or push data directly to a Power BI workspace.
 */
export function PowerBI({ report }: { report: Report }) {
  const [mode, setMode] = useState<Mode>('template');

  return (
    <div className="page">
      <header className="intro">
        <h2>Power BI</h2>
        <p>
          Get this analysis into Power BI. Export a template with pre-built measures,
          push data directly to your workspace, or download a Power BI-optimised CSV.
        </p>
      </header>

      <div className="segmented" style={{ alignSelf: 'flex-start' }}>
        <button aria-pressed={mode === 'template'} onClick={() => setMode('template')}>Template</button>
        <button aria-pressed={mode === 'push'} onClick={() => setMode('push')}>Push to workspace</button>
        <button aria-pressed={mode === 'csv'} onClick={() => setMode('csv')}>Download CSV</button>
      </div>

      {mode === 'template' && <TemplatePanel report={report} />}
      {mode === 'push' && <PushPanel report={report} />}
      {mode === 'csv' && <CsvPanel report={report} />}
    </div>
  );
}

function TemplatePanel({ report }: { report: Report }) {
  const [busy, setBusy] = useState(false);
  const [template, setTemplate] = useState<unknown>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  async function generate(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      const r = await api.powerBiTemplate(report.id);
      setTemplate(r.template);
    } catch (e) { setFailure(asFailure(e)); }
    finally { setBusy(false); }
  }

  return (
    <section className="card">
      <header><h2>Power BI Template</h2></header>
      <p className="muted" style={{ marginTop: 0 }}>
        Generate a template manifest with pre-built measures, field types, and suggested visuals.
        Import it into Power BI Desktop alongside the CSV data for a ready-made report.
      </p>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <button className="primary" onClick={generate} disabled={busy}>
        {busy ? <span className="spinner" /> : 'Generate template'}
      </button>

      {template !== null && (
        <div style={{ marginTop: 16 }}>
          <Notice tone="good" message="Template generated. Download the JSON and import into Power BI Desktop." />
          <div style={{ marginTop: 8 }}>
            <button className="ghost" onClick={() => {
              const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = 'powerbi-template.json'; a.click();
              URL.revokeObjectURL(url);
            }}>Download template.json</button>
          </div>
          <pre style={{ marginTop: 12, padding: 12, background: 'var(--sunken)', borderRadius: 6, fontSize: 12, overflow: 'auto', maxHeight: 300 }}>
            {JSON.stringify(template, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
}

function PushPanel({ report }: { report: Report }) {
  const [accessToken, setAccessToken] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [rows, setRows] = useState<'all' | 'sample'>('all');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ datasetId: string; webUrl: string; rowsPushed: number } | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  async function push(): Promise<void> {
    setBusy(true);
    setFailure(null);
    setResult(null);
    try {
      const r = await api.powerBiPush(report.id, { accessToken, workspaceId, rows });
      setResult(r);
    } catch (e) { setFailure(asFailure(e)); }
    finally { setBusy(false); }
  }

  return (
    <section className="card">
      <header><h2>Push to Power BI Workspace</h2></header>
      <p className="muted" style={{ marginTop: 0 }}>
        Push this dataset directly to a Power BI workspace. You need an Azure AD access token
        with the <code>Dataset.ReadWrite.All</code> scope and your workspace ID.
      </p>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
        <label className="field">
          Access token
          <input type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)}
            placeholder="eyJ0eX..." />
          <span className="field-help">Get one from the Azure portal or via <code>az account get-access-token --resource https://analysis.windows.net/powerbi-apis</code></span>
        </label>
        <label className="field">
          Workspace ID
          <input type="text" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
        </label>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <label className="field">
            Rows
            <select value={rows} onChange={(e) => setRows(e.target.value as 'all' | 'sample')}>
              <option value="all">All rows</option>
              <option value="sample">First 5,000</option>
            </select>
          </label>
          <button className="primary" onClick={push} disabled={busy || accessToken.trim() === '' || workspaceId.trim() === ''}>
            {busy ? <span className="spinner" /> : 'Push data'}
          </button>
        </div>
      </div>

      {result !== null && (
        <div style={{ marginTop: 16 }}>
          <Notice tone="good" message={`Pushed ${result.rowsPushed.toLocaleString()} rows to Power BI.`} />
          <div className="row" style={{ marginTop: 8 }}>
            <a href={result.webUrl} target="_blank" rel="noreferrer">
              <button className="primary">Open in Power BI →</button>
            </a>
          </div>
        </div>
      )}
    </section>
  );
}

function CsvPanel({ report }: { report: Report }) {
  return (
    <section className="card">
      <header><h2>Power BI CSV Export</h2></header>
      <p className="muted" style={{ marginTop: 0 }}>
        Download a CSV optimised for Power BI import. Dates are ISO 8601, numbers use
        dot separators, and column names are clean for direct import.
      </p>
      <div className="row" style={{ marginTop: 12 }}>
        <a href={`/api/reports/${report.id}/powerbi/csv`} download>
          <button className="primary">Download Power BI CSV</button>
        </a>
      </div>
    </section>
  );
}

function asFailure(error: unknown): ApiFailure {
  return error instanceof ApiFailure ? error : new ApiFailure('The request failed.', 'Try again.', 0);
}
